import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Wallet, CreditCard, Money, X as XIcon } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";

const STATE = {
  prepaid: { label: "Prepaid", cls: "bg-[#C7E0C2] text-[#2D5A27] border-l-2 border-[#2D5A27]" },
  paid:    { label: "Paid",    cls: "bg-[#DCFCE7] text-[#15803D]" },
  unpaid:  { label: "Unpaid",  cls: "bg-[#FDE2E4] text-[#B42828]" },
  refunded:{ label: "Refunded", cls: "bg-[#E0E7FF] text-[#4338CA]" },
};
const SOURCE_LABEL = { wallet: "Wallet", razorpay: "Razorpay", upi: "UPI", cash: "Cash",
                       card: "Card", bank_transfer: "Bank transfer", manual: "Manual" };
const SOURCE_ICON = { wallet: Wallet, razorpay: CreditCard, card: CreditCard };

const fmtAmt = (a) => (a == null || a === "" ? "—" : `₹${Number(a).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export const PaymentBadge = ({ payment, testid }) => {
  const s = STATE[payment?.state] || STATE.unpaid;
  return (
    <span data-testid={testid}
          className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded font-bold whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  );
};

// Compact multi-line payment summary for list rows.
export const PaymentCell = ({ payment, testid }) => {
  const p = payment || { state: "unpaid" };
  const paid = p.state !== "unpaid";
  const Icon = SOURCE_ICON[p.source] || Money;
  return (
    <div data-testid={testid} className="text-[11px] leading-tight space-y-0.5 min-w-[150px]">
      <div className="flex items-center gap-1.5 flex-wrap">
        <PaymentBadge payment={p} testid={testid && `${testid}-state`}/>
        <span className="font-mono font-semibold text-[#1A1A1A]" data-testid={testid && `${testid}-amount`}>{fmtAmt(p.amount)}</span>
      </div>
      {paid ? (
        <>
          <div className="flex items-center gap-1 text-[#5C5A55]" data-testid={testid && `${testid}-source`}>
            <Icon size={10}/>{SOURCE_LABEL[p.source] || p.source || "—"}
          </div>
          <div className="text-[#8A8780]" data-testid={testid && `${testid}-date`}>{fmtDate(p.paid_at)}</div>
          <div className="font-mono text-[10px] text-[#8A8780] truncate max-w-[200px]" title={p.payment_id || ""}
               data-testid={testid && `${testid}-id`}>
            ID: {p.payment_id || "—"}
          </div>
        </>
      ) : (
        p.expected_source && <div className="text-[#8A8780]">Pay via {SOURCE_LABEL[p.expected_source] || p.expected_source}</div>
      )}
    </div>
  );
};

const SOURCES = ["cash", "upi", "razorpay", "wallet", "card", "bank_transfer"];

export function RecordPaymentDialog({ url, title, defaultAmount, onClose, onSaved }) {
  const [f, setF] = useState({ amount: defaultAmount ?? "", source: "cash", payment_id: "",
                               paid_at: new Date().toISOString().slice(0, 16), prepaid: false, note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF(v => ({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!(Number(f.amount) > 0)) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post(url, { ...f, amount: Number(f.amount),
                            paid_at: f.paid_at ? new Date(f.paid_at).toISOString() : null });
      toast.success("Payment recorded");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const inp = "h-10 w-full px-3 border border-[#E5E2D9] rounded-md text-sm";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="record-payment-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-md p-5 space-y-3 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg">Record payment · {title}</h3>
          <button type="button" onClick={onClose} data-testid="record-payment-close"><XIcon size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-[#5C5A55]">Amount (₹)
            <input type="number" step="0.01" value={f.amount} onChange={set("amount")} className={inp} data-testid="record-payment-amount"/>
          </label>
          <label className="text-xs text-[#5C5A55]">Source
            <select value={f.source} onChange={set("source")} className={inp} data-testid="record-payment-source">
              {SOURCES.map(s => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
            </select>
          </label>
        </div>
        <label className="block text-xs text-[#5C5A55]">Payment ID / reference
          <input value={f.payment_id} onChange={set("payment_id")} placeholder="pay_XXXX / UPI ref" className={inp} data-testid="record-payment-id"/>
        </label>
        <label className="block text-xs text-[#5C5A55]">Payment date
          <input type="datetime-local" value={f.paid_at} onChange={set("paid_at")} className={inp} data-testid="record-payment-date"/>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.prepaid} onChange={set("prepaid")} data-testid="record-payment-prepaid"/> Paid before service (prepaid)
        </label>
        <input value={f.note} onChange={set("note")} placeholder="Note (optional)" className={inp} data-testid="record-payment-note"/>
        <button type="submit" disabled={busy} data-testid="record-payment-submit"
                className="w-full h-10 rounded-md bg-[#B91C1C] hover:bg-[#991B1B] text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <CheckCircle size={14}/>{busy ? "Saving…" : "Mark as paid"}
        </button>
      </form>
    </div>
  );
}
